import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { RecordAttendanceProps } from './interface/RecordAttendanceProps';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const RecordAttendance: React.FC<RecordAttendanceProps> = ({ open, onClose, attendees, onSave }) => {
  const [selectedAttendees, setSelectedAttendees] = useState<Set<number>>(new Set());
  const [status, setStatus] = useState<string>('');
  const [saveClicked, setSaveClicked] = useState(false);
  const [attendanceMap, setAttendanceMap] = useState<Record<number, string>>({});
  const [radioUsed, setRadioUsed] = useState(false);
  const [checkboxUsed, setCheckboxUsed] = useState(false);
  const [dropdownStatusMap, setDropdownStatusMap] = useState<Record<number, string>>({});
  const [dropdownDisabled, setDropdownDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setSelectedAttendees(new Set());
      setStatus('');
      setSaveClicked(false);
      setRadioUsed(false);
      setCheckboxUsed(false);
      setDropdownStatusMap({});
      setDropdownDisabled(false);
      setAttendanceMap({});
    }
  }, [open, attendees]);


  const isEdit = attendees.some((a) => a.participation && a.participation !== 'N/A');

  const handleToggle = (tempId: number) => {
    setSelectedAttendees((prev) => {
      const updated = new Set(prev);
      if (updated.has(tempId)) updated.delete(tempId);
      else updated.add(tempId);
      return updated;
    });
    setCheckboxUsed(true);
    setRadioUsed(false);
  };

  const isSaveDisabled = (
  (!radioUsed && !checkboxUsed) || // initial state
  (radioUsed && Object.keys(attendanceMap).length === 0) || // no radio selected
  (checkboxUsed && (selectedAttendees.size === 0 || !status)) // dropdown with no selection or no checkbox
);


  const handleSelectAll = () => {
    if (selectedAttendees.size === attendees.length) {
      setSelectedAttendees(new Set());
    } else {
      setSelectedAttendees(new Set(attendees.map((a) => a.tempId)));
    }
    setCheckboxUsed(true);
    setRadioUsed(false);
  };

 const handleReset = () => {
  setSelectedAttendees(new Set());
  setStatus(''); 
  setDropdownStatusMap({});
  setAttendanceMap({});
  setDropdownDisabled(false);
  setRadioUsed(false);
  setCheckboxUsed(false);
};


  const handleSave = () => {
    const updated = attendees.map((attendee) => {
      if (selectedAttendees.has(attendee.tempId) || attendanceMap[attendee.tempId]) {
        const participation = attendanceMap[attendee.tempId] || status || attendee.participation;
        return { ...attendee, participation };
      }
      return attendee;
    });
    setSaveClicked(true);
    onSave(updated);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <span style={{ fontWeight: 'bold' }}>{isEdit ? 'Edit Attendance' : 'Record Attendance'}</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            disabled={radioUsed}
            displayEmpty
            sx={{
              mt: 1,
              fontSize: '12px',
              height: '2rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4D6AFF', borderWidth: '2px' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a4a9b7' },
              borderRadius: '8px'
            }}
            fullWidth
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCheckboxUsed(true);
              setRadioUsed(false);
            }}
            renderValue={(selected) =>
              selected ? selected : <span style={{ color: '#a4a9b7' }}>Select Participation</span>
            }
          >
            <MenuItem value="Present">Present</MenuItem>
            <MenuItem value="Absent">Absent</MenuItem>
            <MenuItem value="Facilitator">Facilitator</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: '#E6E9FF', fontWeight: 600 }}>
                  <Checkbox
                    indeterminate={selectedAttendees.size > 0 && selectedAttendees.size < attendees.length}
                    checked={attendees.length > 0 && selectedAttendees.size === attendees.length}
                    onChange={handleSelectAll}
                    disabled={radioUsed}
                  />
                </TableCell>
                <TableCell sx={{ backgroundColor: '#E6E9FF', fontWeight: 600, pl: 2, pr: 2, width: '130px' }}>Name</TableCell>
                <TableCell sx={{ backgroundColor: '#E6E9FF', fontWeight: 600, pl: 2, pr: 2, width: '130px' }}>
                  Participation
                </TableCell>
                <TableCell sx={{ backgroundColor: '#E6E9FF', fontWeight: 600, pl: 6, pr: 6, width: '220px' }}>
                  Mark Attendance
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {attendees.map((attendee) => (
                <TableRow key={attendee.tempId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAttendees.has(attendee.tempId)}
                      onChange={() => handleToggle(attendee.tempId)}
                      disabled={radioUsed}
                    />
                  </TableCell>

                  <TableCell>{attendee.name}</TableCell>

                  <TableCell sx={{ pl: 3, pr: 3 }}>
                    {attendee.participation ? (
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.3,
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#fff',
                          backgroundColor:
                            attendee.participation === 'Present'
                              ? 'green'
                              : attendee.participation === 'Absent'
                                ? 'red'
                                : attendee.participation === 'Facilitator'
                                  ? 'blue'
                                  : 'gray'
                        }}
                      >
                        {attendee.participation}
                      </Box>
                    ) : saveClicked ? (
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.3,
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#fff',
                          backgroundColor: 'gray'
                        }}
                      >
                        N/A
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          pl: 0,
                          pr: 0,
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          fontSize: '14px',
                          color: '#000',
                          ml: '1cm',
                          display: 'inline-block',
                        }}
                      >
                        -
                      </Box>


                    )}
                  </TableCell>

                  <TableCell sx={{ pl: 6, pr: 6 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      alignItems="flex-start"
                    >
                      {['Present', 'Absent', 'Facilitator'].map((option) => (
                        <label
                          key={option}
                          style={{
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: checkboxUsed ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name={`attendance-${attendee.tempId}`}
                            value={option}
                            checked={attendanceMap[attendee.tempId] === option}
                            onChange={() => {
                              setAttendanceMap((prev) => ({
                                ...prev,
                                [attendee.tempId]: option,
                              }));
                              setRadioUsed(true);
                              setCheckboxUsed(false);
                            }}
                            disabled={checkboxUsed}
                            style={{ marginRight: '6px' }}
                          />
                          {option}
                        </label>
                      ))}
                    </Box>


                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

          </Table>
        </Box>

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            onClick={handleReset}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              width: 60
            }}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaveDisabled}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              width: 60,
              backgroundColor: isSaveDisabled ? '#F4F4F4' : '#3B82F6',
              color: isSaveDisabled ? '#999' : '#fff',
              border: isSaveDisabled ? '1px solid #D0D5DD' : 'none',
              '&:hover': {
                backgroundColor: isSaveDisabled ? '#F4F4F4' : '#2563EB'
              }
            }}
          >
            Save
          </Button>



        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default RecordAttendance;
